import * as Sfx from "redux-saga/effects";

import {RouterActions} from "@atg-shared/router";
import {type KambiWidgetApi, getKambiWidgetApi} from "@atg-sport-shared/kambi-widget-api";
import history from "atg-history";

import {
    KAMBI_WAPI_NAVIGATE,
    KAMBI_WAPI_ADD_OUTCOME,
    KAMBI_WAPI_REMOVE_OUTCOME,
    KAMBI_WAPI_FORCE_BETSLIP_SHOW,
    KAMBI_WAPI_PAGE_INFO,
    KAMBI_WAPI_BETSLIP_MAXIMIZED,
    KAMBI_WAPI_ADD_OUTCOMES,
    KAMBI_WAPI_REMOVE_PREPACK_OUTCOME,
    KAMBI_WAPI_ADD_PREPACK_OUTCOME,
} from "../actions";

import type {
    WapiAddOutcomesAction,
    WapiNavigateAction,
    WapiAddOutcomeAction,
    WapiRemoveOutcomeAction,
    WapiForceBetslipShowAction,
    WapiBetslipMaximizedAction,
    WapiAddPrepackOutcomeAction,
    WapiRemovePrepackOutcomeAction,
} from "../actions";

export function* navigate({path}: WapiNavigateAction): Generator<Sfx.Effect, void, any> {
    const wapi: KambiWidgetApi = yield Sfx.call(getKambiWidgetApi);
    if (!path.startsWith("http"))
        yield Sfx.call(wapi.navigateClient, path, "", {replace: true});
}

function* addOutcome({
    outcomeId,
    source,
}: WapiAddOutcomeAction): Generator<Sfx.Effect, void, any> {
    const wapi: KambiWidgetApi = yield Sfx.call(getKambiWidgetApi);

    yield Sfx.call(wapi.set, wapi.BETSLIP_OUTCOMES, {
        updateMode: wapi.BETSLIP_OUTCOMES_ARGS.UPDATE_APPEND,
        outcomes: [outcomeId],
        source,
    });
}

function* removeOutcome({
    outcomeId,
}: WapiRemoveOutcomeAction): Generator<Sfx.Effect, void, any> {
    const wapi: KambiWidgetApi = yield Sfx.call(getKambiWidgetApi);
    yield Sfx.call(wapi.set, wapi.BETSLIP_OUTCOMES_REMOVE, {outcomes: [outcomeId]});
}

function* addOutcomes({
    outcomeIds,
    source,
}: WapiAddOutcomesAction): Generator<Sfx.Effect, void, any> {
    const wapi: KambiWidgetApi = yield Sfx.call(getKambiWidgetApi);

    yield Sfx.call(wapi.set, wapi.BETSLIP_OUTCOMES, {
        updateMode: wapi.BETSLIP_OUTCOMES_ARGS.UPDATE_APPEND,
        outcomes: outcomeIds,
        source,
    });
}
function* addPrepackOutcome({
    outcomeId,
    source,
}: WapiAddPrepackOutcomeAction): Generator<Sfx.Effect, void, any> {
    const wapi: KambiWidgetApi = yield Sfx.call(getKambiWidgetApi);

    yield Sfx.call(wapi.set, wapi.BETSLIP_PREPACKS, {
        prePacksIds: [outcomeId],
        source,
    });
}

function* removePrepackOutcome({
    outcomeId,
}: WapiRemovePrepackOutcomeAction): Generator<Sfx.Effect, void, any> {
    const wapi: KambiWidgetApi = yield Sfx.call(getKambiWidgetApi);

    yield Sfx.call(wapi.set, wapi.BETSLIP_PREPACKS_REMOVE, {prePacksIds: [outcomeId]});
}

const isAllowedLocation = ({pathname}: {pathname: string}) =>
    !pathname.startsWith("/sport/big9") &&
    !pathname.startsWith("/sport/daily5") &&
    (pathname.startsWith("/sport") || pathname.toLowerCase().includes("/h2h"));

export function* betslipToggler(): Generator<Sfx.Effect, void, any> {
    const wapi: KambiWidgetApi = yield Sfx.call(getKambiWidgetApi);

    let wasAllowed = isAllowedLocation(history.location);

    wapi.set(wasAllowed ? wapi.BETSLIP_SHOW : wapi.BETSLIP_HIDE);

    while (true) {
        const isAllowed = isAllowedLocation(
            (yield Sfx.take(RouterActions.ROUTER_ON_LOCATION_CHANGED)).payload.location,
        );

        if (wasAllowed !== isAllowed)
            wapi.set(isAllowed ? wapi.BETSLIP_SHOW : wapi.BETSLIP_HIDE);

        wasAllowed = isAllowed;
    }
}

function* forceBetslipShow(
    action: WapiForceBetslipShowAction,
): Generator<Sfx.Effect, void, any> {
    const wapi: KambiWidgetApi = yield Sfx.call(getKambiWidgetApi);

    wapi.set(action.hide ? wapi.BETSLIP_HIDE : wapi.BETSLIP_SHOW);
}

function* betslipMaximized(
    action: WapiBetslipMaximizedAction,
): Generator<Sfx.Effect, void, any> {
    const wapi: KambiWidgetApi = yield Sfx.call(getKambiWidgetApi);

    wapi.set(wapi.BETSLIP_MAXIMIZED, {
        maximized: action.maximized,
    });
}

function* requestPageInfo(): Generator<Sfx.Effect, void, any> {
    const wapi: KambiWidgetApi = yield Sfx.call(getKambiWidgetApi);

    wapi.request(wapi.PAGE_INFO);
}

export function* kambiWapiSaga(): Generator<Sfx.Effect, void, any> {
    yield Sfx.takeLatest(KAMBI_WAPI_NAVIGATE, navigate);

    yield Sfx.takeEvery(KAMBI_WAPI_ADD_OUTCOME, addOutcome);
    yield Sfx.takeEvery(KAMBI_WAPI_REMOVE_OUTCOME, removeOutcome);

    yield Sfx.takeEvery(KAMBI_WAPI_ADD_OUTCOMES, addOutcomes);

    yield Sfx.takeEvery(KAMBI_WAPI_ADD_PREPACK_OUTCOME, addPrepackOutcome);
    yield Sfx.takeEvery(KAMBI_WAPI_REMOVE_PREPACK_OUTCOME, removePrepackOutcome);

    yield Sfx.takeLeading(KAMBI_WAPI_FORCE_BETSLIP_SHOW, forceBetslipShow);
    yield Sfx.takeLeading(KAMBI_WAPI_BETSLIP_MAXIMIZED, betslipMaximized);

    yield Sfx.debounce(25, KAMBI_WAPI_PAGE_INFO, requestPageInfo);

    yield Sfx.fork(betslipToggler);
}
